/**
 * The layer panel's discrete operations expressed as intents (ADR-009 §3.4).
 *
 * Each builder reproduces one direct command the panel used to execute, with
 * the same history label; `user-dispatch.test.ts` proves byte parity for
 * every one of them against that command. Operations with no intent keep
 * their native command in LayerPanel.tsx.
 */
import type { Intent, LayerId, Placement } from '@maipo/agent-protocol';
import { findLayer, findLayerParent, type BlendMode, type EditorDocument, type Layer } from '../core/model';
import type { LayerStyle } from '../core/layer-styles';
import type { DocumentSession } from '../core/session';
import { dispatchUser, type UserDispatchOutcome } from '../semantic/user-dispatch';

export interface PanelOperation { intents: Intent[]; label: string }

export function runPanelOperation(session: DocumentSession, operation: PanelOperation | null): UserDispatchOutcome | null {
  return operation ? dispatchUser(session, operation.intents, operation.label) : null;
}

export function renameLayer(layerId: LayerId, name: string): PanelOperation {
  return { intents: [{ type: 'layer.update', layer: layerId, set: { name } }], label: 'Rename layer' };
}

export function toggleVisibility(layer: Layer): PanelOperation {
  return { intents: [{ type: 'layer.update', layer: layer.id, set: { visible: !layer.visible } }], label: layer.visible ? 'Hide layer' : 'Show layer' };
}

/** One entry per selected layer, as the composite controls always recorded. */
export function compositeUpdates(layerIds: readonly LayerId[], set: { blendMode?: BlendMode; opacity?: number }, label: string): PanelOperation[] {
  return layerIds.map((layer) => ({ intents: [{ type: 'layer.update', layer, set: { ...set } }], label }));
}

/**
 * `ReorderLayerCommand` takes a pre-removal insertion gap; a Placement index
 * is the final index. Within the same parent a gap past the layer's current
 * index lands one lower once the layer is lifted out.
 */
export function placementForGap(document: EditorDocument, layerId: LayerId, parentId: LayerId, gap: number): Placement {
  const parent = parentId === 'root' || parentId === document.root.id ? document.root : findLayer(document.root, parentId);
  const current = parent?.kind === 'group' ? parent.children.findIndex((child) => child.id === layerId) : -1;
  return { parent: parentId, index: current >= 0 && gap > current ? gap - 1 : gap };
}

export function reorderToGap(document: EditorDocument, layerId: LayerId, parentId: LayerId, gap: number): PanelOperation {
  return { intents: [{ type: 'layer.reorder', layer: layerId, to: placementForGap(document, layerId, parentId, gap) }], label: 'Reorder layer' };
}

/** Drag-and-drop onto a row: into a group (top of its children), or above or below the row. */
export function dropLayer(document: EditorDocument, draggedId: LayerId, target: Layer, where: 'above' | 'below' | 'into'): PanelOperation | null {
  if (where === 'into' && target.kind === 'group') return { intents: [{ type: 'layer.reorder', layer: draggedId, to: { parent: target.id, index: 0 } }], label: 'Reorder layer' };
  if (!findLayerParent(document.root, target.id)) return null;
  return { intents: [{ type: 'layer.reorder', layer: draggedId, to: where === 'above' ? { above: target.id } : { below: target.id } }], label: 'Reorder layer' };
}

export function removeLayer(layerId: LayerId): PanelOperation {
  return { intents: [{ type: 'layer.delete', layers: [layerId] }], label: 'Remove layer' };
}

/** GroupLayersCommand ignored IDs that no longer exist; the intent would refuse them, so they are left out here. */
export function groupLayers(document: EditorDocument, layerIds: readonly LayerId[]): PanelOperation | null {
  const layers = layerIds.filter((id) => findLayerParent(document.root, id));
  return layers.length ? { intents: [{ type: 'layer.group', layers }], label: 'Group layers' } : null;
}

export function ungroupLayer(groupId: LayerId): PanelOperation {
  return { intents: [{ type: 'layer.ungroup', group: groupId }], label: 'Ungroup layers' };
}

export function setClipping(layerId: LayerId, enabled: boolean): PanelOperation {
  return { intents: [{ type: 'mask.clip', layer: layerId, enabled }], label: enabled ? 'Clip to layer below' : 'Release clipping' };
}

export function setMaskEnabled(layerId: LayerId, enabled: boolean): PanelOperation {
  return { intents: [{ type: 'mask.update', layer: layerId, enabled }], label: 'Update layer mask' };
}

export function invertMask(layerId: LayerId): PanelOperation {
  return { intents: [{ type: 'mask.invert', layer: layerId }], label: 'Invert layer mask' };
}

export function removeMask(layerId: LayerId): PanelOperation {
  return { intents: [{ type: 'mask.remove', layer: layerId }], label: 'Remove layer mask' };
}

export function setDropShadow(layerId: LayerId, value: NonNullable<LayerStyle['dropShadow']>): PanelOperation {
  return { intents: [{ type: 'effect.set', layer: layerId, effect: 'dropShadow', value: { ...value } }], label: 'Change layer style' };
}

export function setStrokeEffect(layerId: LayerId, value: NonNullable<LayerStyle['stroke']>): PanelOperation {
  return { intents: [{ type: 'effect.set', layer: layerId, effect: 'stroke', value: { ...value } }], label: 'Change layer style' };
}

/** "New raster layer" (+): a transparent layer of the document's size on top of the root, as `AddLayerCommand(createRasterLayer(...))` did. */
export function newRasterLayer(document: EditorDocument): PanelOperation {
  return { intents: [{ type: 'layer.create', spec: { kind: 'raster', name: 'New layer', width: document.width, height: document.height } }], label: 'Add New layer' };
}
